import { useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { GET_CATEGORIES, GET_ORG_DETAILS, GET_ORG_PATH_ID } from "../../graphql/org-gql";
import { ICategoriesResponse, ICategory, IOrg, IOrgPathIdResponse, IOrgResponse, isCategory } from "../../types/org";
import CategoryInfo from "../category/category-info";
import AccordionContainer from "../common/accordion-container";
import CollapsablePanel from "../common/collapsable-panel/collapsable-panel";
import OrgTree from '../common/org-tree/org-tree';
import SpinnerOverlay from "../common/spinner/spinner";
import TabsWithDropDown, { TabDropDownItem, TabWithDropDown } from "../common/tabs-with-dropdown/tabs-with-dropdown";
import Individual from "../individual/individual";
import PositionDetailsInfo from "../position/position";
import RoleDetailsInfo from "../role/role";
import SearchPositionRole, { SearchPositionRoleRef } from "../search/search-position-role/search-position-role";
import OrgInfo from "./org-info";
import SearchIndividual, { SearchIndividualRef } from "../search/search-individual/search-individual";
import SearchPositionOrgInfo from "../search/search-position-role/search-position-org-info";
import { Alert } from "reactstrap";
import { InfoIcon } from "../common/icons";
import SearchOrg, { SearchOrgRef } from "../search/search-org/search-org";
import { OrgReducer, OrgReducerContext, OrgState, messageSearch, orgReducer } from "./org-context";
import './org.scss';
import useScrollIntoView from "../../hook/useScrollIntoView";
import Spacer from "../common/spacer";
import { IIndividual } from "../../types/individual";

const Org = () => {
  const [treeSelected, setTreeSelected] = useState<ICategory | IOrg>()
  const ref = useRef<OrgTreeRef | null>(null);
  const searchOrgRef = useRef<SearchOrgRef | null>(null);
  const searchPositionRoleRef = useRef<SearchPositionRoleRef | null>(null);
  const searchIndividualRef = useRef<SearchIndividualRef | null>(null);
  const [inOrgId, setInOrgId] = useState<number>()
  const [state, dispatch] = useReducer(orgReducer, {} as OrgState);
  const [getOrgPath] = useLazyQuery<IOrgPathIdResponse>(GET_ORG_PATH_ID)
  const [orgPathIds, setOrgPathIds] = useState<number[]>([])
  const [selectedTabDropDownItem, setSelectedTabDropDownItem] = useState<TabDropDownItem | undefined>()

  const treeSelectedRef = useRef<HTMLDivElement>(null);
  const orgSearchRef = useRef<HTMLDivElement>(null);
  const positionSearchRef = useRef<HTMLDivElement>(null);
  const individualSearchRef = useRef<HTMLDivElement>(null);
 
  const contextValue = useMemo<OrgReducer>(
    () => ([state, dispatch]),
    [state]
  );

  // Define a type for the ref object
  type OrgTreeRef = {
    refresh: () => void;
  };
  const { loading, error, data, refetch } = useQuery<ICategoriesResponse>(GET_CATEGORIES)

  const [getOrgDetail] = useLazyQuery<IOrgResponse>(GET_ORG_DETAILS)

  useScrollIntoView(treeSelectedRef, [
    treeSelected
  ]);

  useScrollIntoView(individualSearchRef, [
    state.individualSearchIndividual,
    state.individualSearchOrg,
    state.individualSearchPosition,
    state.individualSearchTitle
  ]);

  useScrollIntoView(positionSearchRef, [
    state.positionSearchIndividual,
    state.positionSearchPositionRole,
    state.positionSearchPosition,
    state.positionSearchOrg
  ]);

  useScrollIntoView(orgSearchRef, [
    state.orgSearchOrg
  ]);

  const selectTreeItem = (item?: IOrg | ICategory) => {
    //console.log("selected item:",item)
    if (item) {
      if (!isCategory(item)) {
        getOrgDetail({ variables: { id: item.id } ,fetchPolicy: "network-only" }).then((response) => {
          response?.data?.org && setTreeSelected(response.data.org)
        })
      }
      setTreeSelected(item)
    }
  }

  //useEffect(()=>{console.log("state:",state)},[state])
  useEffect(() => {
    //console.log("in orgId " + inOrgId);
    if(!inOrgId){
      try {
        const value = sessionStorage.getItem("orgId")
        if (value) {
          const parsedValue = JSON.parse(value)
         setInOrgId(parsedValue)
       
        } else {
          setInOrgId(undefined)
        }
      } catch (error) {
        setInOrgId(undefined)
      }
    }
    if(inOrgId){
      
      getOrgPath({ variables: { id: inOrgId } ,fetchPolicy: "network-only" }).then((resp)=>{
        console.log("====" + JSON.stringify(resp?.data))
        if (resp?.data?.orgPathId) {
         // const newIds = resp.data.orgPathId.map((id: number) => Math.abs(id));
          setOrgPathIds(resp?.data?.orgPathId);
        }
        
      })
       getOrgDetail({ variables: { id: inOrgId } }).then((response) => {
         setTreeSelected(response?.data?.org)
         sessionStorage.removeItem("orgId")
       })
    }
  },[inOrgId])

  if (error) {
    console.error('Error loading top orgs: %s', error);
  }
  const refreshHandle = () => {
    refetch();
    ref.current?.refresh();
    setTreeSelected(undefined);
  }

  const onItemClickhandle = (item:TabDropDownItem) => {
    setTreeSelected(undefined);
    setSelectedTabDropDownItem(item);
    dispatch({type:'reset'})

    if(item.itemTitle==='Organization') {
      dispatch({
        type:'set-org-search-message',
        payload:messageSearch
      })
    }

    if(item.itemTitle==='Individual') {
      dispatch({
        type:'set-individual-search-message',
        payload:messageSearch
      })
    }

    if(item.itemTitle==='Position/Role') {
      dispatch({
        type:'set-position-search-message',
        payload:messageSearch
      })
    }

  }

  const onSearchOrgSelect = (org: IOrg | undefined) => {
    dispatch({
      type:'set-org-search-org',
      payload: org
    });
    dispatch({type:'set-org-search-message'})
    dispatch({type:'set-org-search-error'})

  }

  function getIndividual(): IIndividual | undefined {
    if(state.lastSelectedItem && typeof state.lastSelectedItem === "object" && "individualId" in state.lastSelectedItem) 
      return state.lastSelectedItem as IIndividual;

    if (state.individualSearchIndividual)
      return state.individualSearchIndividual;

    if (state.positionSearchIndividual)
      return state.positionSearchIndividual;

    return undefined;
  }

  const tabsWihtDropDownList: TabWithDropDown[] = [

    {
      title: 'Hierarchy Tree',
      items: [
        {
          content:
            <CollapsablePanel headTitle="Organizations within your scope"
              initCollapse={false} css_classes="hide-heading"
              helpUrl="https://intradev.sus.ebc.gov.on.ca/oms/modal/general/help/TreeOrg">
              {loading && <SpinnerOverlay />}
              {error && <div className="alert alert-danger" role="alert"><small>Error loading organizations</small></div>}
             
              {!loading && !error && <OrgTree roots={data?.categories || []} isGlobal={false} onSelected={selectTreeItem} ref={ref} orgPathIds={orgPathIds} inOrgId={inOrgId}></OrgTree>}
            </CollapsablePanel>
        }

      ]
    },
    {
      title: 'Search By',
      items: [
        {
          itemTitle: 'Organization',
          content: <SearchOrg  ref={searchOrgRef} />

        },
        {
          itemTitle: 'Position/Role',
          content: <SearchPositionRole  ref={searchPositionRoleRef} />
        },
        {
          itemTitle: 'Individual',
          content: <SearchIndividual  ref={searchIndividualRef} />
        }
      ]
    }

  ]

  return (
    <OrgReducerContext.Provider value={contextValue}>
      <h1 ><div role="region" aria-label="Organization Tree" className="hidden-text">Organization Tree </div></h1>

      <div className="org-top mb-3">
        <AccordionContainer title="Find what you're looking for within your scope">
          <div className="row">
            <div className="col-lg-12">
              <section className="org-hierarchy">
                {/*<Tabs tabs={tabs} hasRefresh={true} refreshHandle={refreshHandle}/>*/}
                <TabsWithDropDown tabs={tabsWihtDropDownList} hasRefresh={true} refreshHandle={refreshHandle} onItemClickhandle={onItemClickhandle} />

              </section>
            </div>
          </div>
        </AccordionContainer>
      </div>
      <div ref={treeSelectedRef}>
      {
        treeSelected && (
          isCategory(treeSelected) ?
            <CategoryInfo category={treeSelected} />
            :
            <OrgInfo  org={treeSelected} onOrgSelect={setTreeSelected} />
        )
      }
      </div>

      { selectedTabDropDownItem?.itemTitle === 'Organization' && 
        <div ref={orgSearchRef} id="organization-search-container">
          { state.orgSearchOrg && <OrgInfo org={state.orgSearchOrg} onOrgSelect={onSearchOrgSelect} /> }
        </div>
      }
      
      { selectedTabDropDownItem?.itemTitle === 'Position/Role' && 
        <div ref={positionSearchRef} id="position-search-container">
          { state.positionSearchPosition && !state.positionSearchPosition.isRole && <PositionDetailsInfo position={state.positionSearchPosition} /> }
          { state.positionSearchPosition && state.positionSearchPosition.isRole && <RoleDetailsInfo role={state.positionSearchPosition} /> }
          { state.positionSearchIndividual && <Individual org={state.positionSearchOrg} position={state.positionSearchPositionRole} individual={state.positionSearchIndividual} /> }
          <Spacer/>
          { state.positionSearchOrg  && <SearchPositionOrgInfo org={state.positionSearchOrg}  />}
        </div>
      }

      { selectedTabDropDownItem?.itemTitle === 'Individual' && 
        <div ref={individualSearchRef} id="individual-search-container">
          { (state.positionSearchPosition && !state.positionSearchPosition.isRole) && <PositionDetailsInfo position={state.positionSearchPosition} /> }
          { (state.positionSearchPosition && state.positionSearchPosition.isRole) && <RoleDetailsInfo role={state.positionSearchPosition} /> }
          { getIndividual() && <Individual org={state.individualSearchOrg} position={state.individualSearchPosition} individual={getIndividual()!}/> }
          { state.individualSearchTitle && !state.individualSearchTitle.isRole && <PositionDetailsInfo position={state.individualSearchTitle} /> }
          { state.individualSearchTitle && state.individualSearchTitle.isRole && <RoleDetailsInfo role={state.individualSearchTitle} /> }
          { state.individualSearchOrg && <><Spacer/><SearchPositionOrgInfo org={state.individualSearchOrg} /></> }
        </div>
      }

      {state.orgSearchMessage  &&
        <Alert  color="info">
          <InfoIcon/> {state.orgSearchMessage}
        </Alert>

      }

      {state.orgSearchError  &&
        <Alert  color="danger">
          <InfoIcon/>
          <span dangerouslySetInnerHTML={{ __html: state.orgSearchError }} />
        </Alert>

      }

      {state.individualSearchMessage  &&
        <Alert  color="info">
          <InfoIcon/> {state.individualSearchMessage}
        </Alert>

      }

      {state.individualSearchError  &&
        <Alert  color="danger">
          <InfoIcon/> 
          <span dangerouslySetInnerHTML={{ __html: state.individualSearchError }} />
        </Alert>

      }

      {state.positionSearchMessage  &&
        <Alert  color="info">
          <InfoIcon/> {state.positionSearchMessage}
        </Alert>

      }

      {state.positionSearchError  &&
        <Alert  color="danger">
          <InfoIcon/> 
          <span dangerouslySetInnerHTML={{ __html: state.positionSearchError }} />
        </Alert>

      }
    </OrgReducerContext.Provider>
  );
};

export default Org;
